import { useEffect, useState } from 'react';

/**
 * Returns a debounced copy of `value` that only updates after `delayMs`
 * milliseconds of inactivity. Used to avoid firing an API call on every
 * keystroke in search inputs.
 *
 * @param {*} value     The live value (usually a string from an input).
 * @param {number} delayMs  Debounce delay in ms (default 300).
 * @returns {*}         The debounced value.
 *
 * @example
 *   const [search, setSearch] = useState('');
 *   const debouncedSearch = useDebouncedValue(search, 300);
 *   useEffect(() => { fetchLeads({ search: debouncedSearch }); },
 *             [debouncedSearch]);
 */
export default function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
